import React, { useMemo, useState } from "react";
import Papa from "papaparse";
import "../Stylesheets/CSVProcessor.css";

function CSVProcessor() {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [data, setData] = useState([]);
  const [downloadFileName, setDownloadFileName] = useState("deduplicated_output.csv");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setStatus("");
    setData([]);
    setCurrentPage(1);
  };

  const processCSV = () => {
    if (!file) {
      setStatus("Please select a CSV file.");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedData = results.data;

        const dedupedMap = new Map(); // key = owner name OR address
        const countMap = new Map(); // key = dedup key => count

        for (const row of parsedData) {
          const owner = row["Owner Name 1"]?.trim();
          if (!owner) continue;

          // Fix PO BOX logic
          if (row["Street Name"]?.toUpperCase().includes("PO BOX")) {
            row["Street Name"] = `PO BOX ${row["House Number"]}`;
            row["House Number"] = "";
          }

          const fullAddress = [
            row["House Number"],
            row["Prefix Direction"],
            row["Street Name"],
            row["Street Type"],
            row["Post Direction"],
            row["Unit Type"],
            row["Unit Number"]
          ]
            .filter(Boolean)
            .map((s) => s.trim().toUpperCase())
            .join(" ")
            .replace(/\s+/g, " ");

          const ownerKey = owner.toUpperCase();
          const addressKey = fullAddress;

          // Pick whichever key is more specific (prefer full address if available)
          const dedupKey = addressKey || ownerKey;

          if (!dedupedMap.has(dedupKey)) {
            dedupedMap.set(dedupKey, {
              Recipient: owner,
              Company: row["Owner Name 2"] || "",
              Address: addressKey,
              "City Name": row["City Name"] || "",
              "State Abbreviation": row["State Abbreviation"] || "",
              "Zip Code": row["Zip Code"] || "",
              "Properties Owned": 1,
            });
            countMap.set(dedupKey, 1);
          } else {
            // Increment properties owned
            countMap.set(dedupKey, countMap.get(dedupKey) + 1);
          }
        }

        // Assign updated property counts
        for (const [key, entry] of dedupedMap.entries()) {
          entry["Properties Owned"] = countMap.get(key);
        }

        const deduplicatedData = Array.from(dedupedMap.values());

        if (deduplicatedData.length === 0) {
          setStatus("No valid data found in the CSV.");
          return;
        }

        setData(deduplicatedData);
        setStatus(`CSV processed successfully! Trimmed to ${deduplicatedData.length} unique entries.`);
        setCurrentPage(1);
      },
      error: (error) => {
        setStatus(`Error parsing CSV: ${error.message}`);
      },
    });
  };

  const downloadCSV = () => {
    const csvContent = Papa.unparse(sortedData);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = downloadFileName || "deduplicated_output.csv";
    link.click();
  };

  const filteredData = data.filter((row) => {
    const name = row["Recipient"]?.toLowerCase() || "";
    const address = row["Address"]?.toLowerCase() || "";
    return name.includes(searchTerm.toLowerCase()) || address.includes(searchTerm.toLowerCase());
  });

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    const copy = [...filteredData];
    const { key, direction } = sortConfig;
    const isNumeric = key === "Properties Owned";

    copy.sort((a, b) => {
      const aVal = a?.[key];
      const bVal = b?.[key];
      // Handle empties: push undefined/empty to bottom
      if (aVal == null || aVal === "") return 1;
      if (bVal == null || bVal === "") return -1;

      if (isNumeric) {
        const aNum = Number(aVal) || 0;
        const bNum = Number(bVal) || 0;
        return aNum - bNum;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return aStr.localeCompare(bStr);
    });

    return direction === "asc" ? copy : copy.reverse();
  }, [filteredData, sortConfig]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentData = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const changePage = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const sortableKeys = new Set(["Address", "Recipient", "Properties Owned"]);

  const handleSort = (header) => {
    if (!sortableKeys.has(header)) return;
    setSortConfig((prev) => {
      if (prev.key === header) {
        return { key: header, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key: header, direction: "asc" };
    });
    setCurrentPage(1);
  };

  return (
    <div className="container mt-4 p-3 bg-light shadow rounded">
      <h4 className="text-center mb-3">CSV Deduplication Tool</h4>
      <p className="text-center mb-3">Download: Owner Address Layout</p>

      <div className="mb-2">
        <input type="file" className="form-control form-control-sm" accept=".csv" onChange={handleFileChange} />
      </div>

      <div className="d-flex justify-content-center mb-2">
        <button className="btn btn-primary btn-sm px-3" onClick={processCSV}>
          Process CSV
        </button>
      </div>

      {status && (
        <p className={`text-center fw-bold ${data.length > 0 ? "text-success" : "text-danger"}`}>{status}</p>
      )}

      {data.length > 0 && (
        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <input
              type="text"
              className="form-control form-control-sm me-2"
              style={{ maxWidth: "250px" }}
              placeholder="Search by Name or Address..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <div className="d-flex align-items-center">
              <input
                type="text"
                className="form-control form-control-sm me-2"
                style={{ maxWidth: "200px" }}
                placeholder="Save file name as..."
                value={downloadFileName}
                onChange={(e) => setDownloadFileName(e.target.value)}
              />
              <button className="btn btn-success btn-sm" onClick={downloadCSV}>
                Download CSV
              </button>
            </div>
          </div>

          <div className="d-flex justify-content-end mb-2">
            <label className="me-2 small">Rows per page:</label>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="form-select form-select-sm"
              style={{ width: "80px" }}
            >
              {[15, 25, 50, 75, 100].map((amount) => (
                <option key={amount} value={amount}>
                  {amount}
                </option>
              ))}
            </select>
          </div>

          {sortedData.length > 0 ? (
            <div className="table-responsive compact-table">
              <table className="table table-sm table-striped table-hover">
                <thead className="table-dark small">
                  <tr>
                    {Object.keys(data[0]).map((header) => {
                      const isSortable = sortableKeys.has(header);
                      const isActive = sortConfig.key === header;
                      const arrow = isActive ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : "";
                      return (
                        <th
                          key={header}
                          onClick={() => handleSort(header)}
                          style={{ cursor: isSortable ? "pointer" : "default" }}
                          title={isSortable ? `Sort by ${header}` : undefined}
                        >
                          {header}
                          {isSortable && <span>{arrow}</span>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="small">
                  {currentData.map((row, index) => (
                    <tr key={index}>
                      {Object.keys(row).map((field, idx) => (
                        <td key={idx}>{row[field]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-muted">No matches found for "{searchTerm}".</p>
          )}

          <div className="d-flex justify-content-between align-items-center mt-2 small">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => changePage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => changePage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CSVProcessor;
